import { Alert, Intent, Spinner } from "@blueprintjs/core"
import { useMemo, useState } from "react"

import { IconNames } from "@blueprintjs/icons"
import { AxiosError } from "axios"
import { MediumItalicText } from "../../../../styled/text.styled"
import { createToaster } from "../../../utils/createToaster"
import ReplaceOrderModal from "../../PortfolioManagement/Trading/ReplaceOrderModal"
import { Order } from "../../PortfolioManagement/Trading/types/order"
import Grid from "../components/Grid"
import { IContextMenuItemDef } from "../components/hooks/useAgGrid"
import { ContextMenuItem, DataframeData } from "../types"
import { RefreshButton } from "./components/RefreshButton"
import { useCancelOrderMutation } from "./hooks/useCancelOrderMutation"
import useIntervalDateDistance from "./hooks/useIntervalDateDistance"
import { NoDataMessage, OrdersContainer } from "./Orders.styled"

interface IOrdersProps {
  orders?: DataframeData
  isLoading: boolean
  shortLocateAction?: (rowData: any) => void
}

const Orders = ({ orders, isLoading, shortLocateAction }: IOrdersProps) => {
  // State to manage alert visibility and order details
  const [isAlertOpen, setIsAlertOpen] = useState(false)
  const [orderToCancel, setOrderToCancel] = useState<Partial<Order> | null>(null)

  // State for replace order modal
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false)
  const [replaceOrderId, setReplaceOrderId] = useState<string | null>(null)

  // Get the cancel order mutation
  const { mutate: cancelOrder } = useCancelOrderMutation({
    onSuccess: data => {
      if (data.success) {
        createToaster({
          message: `Order cancelled successfully`,
          intent: Intent.SUCCESS,
        })
      } else {
        createToaster({
          message: `Failed to cancel order: ${data.description}`,
          intent: Intent.DANGER,
        })
      }
    },
    onError: (error: AxiosError) => {
      createToaster({
        message: `Error cancelling order: ${error.message}`,
        intent: Intent.DANGER,
      })
    },
  })

  // Calculate the time difference for the last updated info
  const ordersDateDistance = useIntervalDateDistance({
    date: orders?.last_updated_at,
    interval: 60 * 1000,
  })

  /**
   * Handler for cancel order action from context menu
   */
  const handleCancelOrderRequest = (rowData: any) => {
    const order: Partial<Order> = {
      flex_order_id: rowData["order_id"],
      side: rowData["side"],
      quantity: rowData["quantity"],
      instrument: {
        bloomberg_code: rowData["bb_symbol"],
      },
    }
    setOrderToCancel(order)
    setIsAlertOpen(true)
  }

  /**
   * Check if the order can be canceled based on the row data
   */
  const canCancelOrder = (rowData: any) => {
    return (
      rowData &&
      "order_id" in rowData &&
      typeof rowData["order_id"] === "string" &&
      rowData["order_id"].length > 0 &&
      !["Canceled", "Filled"].includes(rowData["status"])
    )
  }

  /**
   * Handler for replace order action from context menu
   */
  const handleReplaceOrderRequest = (rowData: any) => {
    if (rowData && "order_id" in rowData && typeof rowData["order_id"] === "string") {
      setReplaceOrderId(rowData["order_id"])
      setIsReplaceModalOpen(true)
    }
  }

  /**
   * Check if the order can be replaced based on the row data
   */
  const canReplaceOrder = (rowData: any) => {
    return (
      rowData &&
      "order_id" in rowData &&
      typeof rowData["order_id"] === "string" &&
      rowData["order_id"].length > 0 &&
      !["Canceled", "Locate Failed", "Filled"].includes(rowData["status"])
    )
  }

  /**
   * Handler for cancel order confirmation
   */
  const onCancelConfirm = () => {
    if (orderToCancel?.flex_order_id) {
      cancelOrder({ orderId: orderToCancel.flex_order_id })
    }
    setIsAlertOpen(false)
    setOrderToCancel(null)
  }

  /**
   * Check if the order can be short located based on the row data
   */
  const canShortLocate = (rowData: any) => {
    return (
      rowData &&
      "status" in rowData &&
      ["Locate Failed", "Locate Pending", "Locate Submitted"].includes(rowData["status"])
    )
  }

  /**
   * Memoized context menu items definition
   */
  const contextMenuItemsDef: IContextMenuItemDef[] | undefined = useMemo(() => {
    if (!orders?.context_menu_items) return undefined

    const orderedItems: IContextMenuItemDef[] = []

    // First add CANCEL_ORDER if present
    if (orders.context_menu_items.includes(ContextMenuItem.CANCEL_ORDER)) {
      orderedItems.push({
        contextMenuItem: ContextMenuItem.CANCEL_ORDER,
        action: handleCancelOrderRequest,
        shouldEnableMenuItem: canCancelOrder,
      })
    }

    // Then add REPLACE_ORDER if present
    if (orders.context_menu_items.includes(ContextMenuItem.REPLACE_ORDER)) {
      orderedItems.push({
        contextMenuItem: ContextMenuItem.REPLACE_ORDER,
        action: handleReplaceOrderRequest,
        shouldEnableMenuItem: canReplaceOrder,
      })
    }

    // Then add MANUAL_LOCATE if present
    if (orders.context_menu_items.includes(ContextMenuItem.FORCE_LOCATE)) {
      orderedItems.push({
        contextMenuItem: ContextMenuItem.FORCE_LOCATE,
        action: shortLocateAction,
        shouldEnableMenuItem: canShortLocate,
      })
    }

    // Add separator
    orderedItems.push({
      contextMenuItem: ContextMenuItem.SEPARATOR,
    })

    // Add remaining default items
    orders.context_menu_items.forEach(item => {
      if (
        item !== ContextMenuItem.CANCEL_ORDER &&
        item !== ContextMenuItem.REPLACE_ORDER &&
        item !== ContextMenuItem.FORCE_LOCATE
      ) {
        orderedItems.push({
          contextMenuItem: item,
        })
      }
    })

    return orderedItems
  }, [orders?.context_menu_items, shortLocateAction])

  return (
    <>
      <OrdersContainer
        flexGrow={1}
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        {orders ? (
          <Grid
            rows={orders.rows}
            columns={orders.columns}
            rowStyles={orders.row_styles}
            label="Orders"
            contextMenuItemsDef={contextMenuItemsDef || []}
            columnSizingStrategy="autoSize"
          >
            {ordersDateDistance && (
              <>
                <MediumItalicText>Last updated {ordersDateDistance}</MediumItalicText>
                <RefreshButton lastUpdatedAt={orders?.last_updated_at} />
              </>
            )}
          </Grid>
        ) : isLoading ? (
          <Spinner />
        ) : (
          <NoDataMessage>No orders available, please select an account and/or a book</NoDataMessage>
        )}
      </OrdersContainer>

      {/* Render the alert with enhanced order details */}
      <Alert
        isOpen={isAlertOpen}
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        onConfirm={onCancelConfirm}
        onCancel={() => setIsAlertOpen(false)}
        icon={IconNames.CROSS_CIRCLE}
        intent={Intent.DANGER}
        className="bp5-dark"
        canOutsideClickCancel
      >
        <p>Are you sure you want to cancel this order?</p>
        {orderToCancel && (
          <div>
            {orderToCancel.instrument?.bloomberg_code && (
              <p>
                <strong>Symbol:</strong> {orderToCancel.instrument.bloomberg_code}
              </p>
            )}
            {orderToCancel.side && (
              <p>
                <strong>Side:</strong> {orderToCancel.side}
              </p>
            )}
            {orderToCancel.quantity && (
              <p>
                <strong>Quantity:</strong> {orderToCancel.quantity}
              </p>
            )}
          </div>
        )}
      </Alert>

      {/* Replace Order Modal */}
      {replaceOrderId && (
        <ReplaceOrderModal
          isOpen={isReplaceModalOpen}
          onClose={() => {
            setIsReplaceModalOpen(false)
            setReplaceOrderId(null)
          }}
          orderId={replaceOrderId}
        />
      )}
    </>
  )
}

export default Orders
